import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

interface AuditLog {
  id: string;
  tenant_id: string;
  user_id?: string;
  action_type: string;
  resource_type: string;
  resource_id?: string;
  details?: any;
  ip_address?: string;
  user_agent?: string;
  success: boolean;
  risk_level: string;
  timestamp: string;
  created_at: string;
}

export function useAuditLogging() {
  const { profile } = useAuth();

  const createAuditLog = async (
    actionType: string,
    resourceType: string,
    resourceId?: string,
    details?: any,
    success: boolean = true,
    riskLevel: 'low' | 'medium' | 'high' | 'critical' = 'low'
  ) => {
    if (!profile?.tenant_id) return;

    // Get client info - IP address captured server-side in Edge Functions for privacy
    const userAgent = navigator.userAgent;

    try {
      const { error } = await supabase.rpc('create_audit_log', {
        p_tenant_id: profile.tenant_id,
        p_user_id: profile.user_id,
        p_action_type: actionType,
        p_resource_type: resourceType,
        p_resource_id: resourceId,
        p_details: details ? JSON.stringify(details) : null,
        p_ip_address: null, // IP captured server-side for privacy
        p_user_agent: userAgent,
        p_success: success,
        p_risk_level: riskLevel
      });

      if (error) {
        console.error('Failed to create audit log:', error);
      }
    } catch (err) {
      console.error('Audit logging error:', err);
    }
  };

  const getAuditLogs = async (
    limit: number = 100,
    offset: number = 0,
    filters?: {
      actionType?: string;
      resourceType?: string;
      riskLevel?: string;
      startDate?: string;
      endDate?: string;
    }
  ) => {
    if (!profile?.tenant_id) return { data: [], error: 'No tenant ID' };

    let query = supabase
      .from('audit_logs')
      .select('*')
      .eq('tenant_id', profile.tenant_id)
      .order('timestamp', { ascending: false })
      .range(offset, offset + limit - 1);

    if (filters?.actionType) {
      query = query.eq('action_type', filters.actionType);
    }
    if (filters?.resourceType) {
      query = query.eq('resource_type', filters.resourceType);
    }
    if (filters?.riskLevel) {
      query = query.eq('risk_level', filters.riskLevel);
    }
    if (filters?.startDate) {
      query = query.gte('timestamp', filters.startDate);
    }
    if (filters?.endDate) {
      query = query.lte('timestamp', filters.endDate);
    }

    return await query;
  };

  return {
    createAuditLog,
    getAuditLogs
  };
}